/**
 * Retour de la connexion Google.
 *
 * L'API renvoie le navigateur sur `/login/google#status=...`. L'etat est dans le
 * FRAGMENT, jamais en query string : App.tsx ouvre le connecteur de depots des
 * qu'un `?state=` ou `?provider=` apparait, et un fragment ne part pas dans les
 * journaux d'acces du serveur statique.
 */

export type GoogleReturnStatus = 'ok' | 'mfa' | 'device' | 'link' | 'linked' | 'error';
export type OidcProvider = 'google' | 'entra';

export interface GoogleReturn {
  status: GoogleReturnStatus;
  token?: string;
  email_hint?: string;
  next?: string;
  code?: string;
}

const STATUSES: GoogleReturnStatus[] = ['ok', 'mfa', 'device', 'link', 'linked', 'error'];

/** Un chemin RELATIF de la console, sinon le tableau de bord (anti open-redirect). */
export const sanitizeNext = (value: string | null | undefined): string => {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.includes('\\')) {
    return '/dashboard';
  }
  return value;
};

/** Meme fragment pour Google et Entra ; seul le code d'erreur par defaut change. */
export const parseOidcReturnFragment = (hash: string, provider: OidcProvider = 'google'): GoogleReturn => {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const params = new URLSearchParams(raw);
  const status = params.get('status') as GoogleReturnStatus | null;
  if (!status || !STATUSES.includes(status)) {
    return { status: 'error', code: `${provider}_unavailable` };
  }
  const out: GoogleReturn = { status };
  const token = params.get('token');
  const hint = params.get('email_hint');
  const code = params.get('code');
  if (token) out.token = token;
  if (hint) out.email_hint = hint;
  if (code) out.code = code;
  if (status === 'ok' || status === 'linked') out.next = sanitizeNext(params.get('next'));
  if ((status === 'mfa' || status === 'device' || status === 'link') && !token) {
    return { status: 'error', code: `${provider}_unavailable` };
  }
  return out;
};

export const parseGoogleReturnFragment = (hash: string): GoogleReturn => parseOidcReturnFragment(hash, 'google');
